<style>
    #terms{
        text-align: left !important;
        direction: ltr !important;
    }
</style>

<template>
  <div id="terms" class="LegalPage py-12 bg-gray-50 min-h-screen">
    <div class="max-w-4xl mx-auto px-4">

      <!-- Page Header -->
      <div class="mb-10 border-b border-gray-200 pb-6">
        <h1 class="text-4xl font-bold text-gray-900 mb-2">Terms of Use</h1>
        <p class="text-sm text-gray-500">Last updated: February 21, 2022</p>
        <p class="mt-3 text-gray-700">
          Please read these Terms and Conditions carefully before using our Service.
        </p>
        <p class="mt-2 text-sm text-gray-600">
          For information on how we handle your data, please review our
          <a href="/privacy" class="text-blue-600 hover:underline font-medium">
            Privacy Policy
          </a>.
        </p>
      </div>

      <!-- Section 1: Interpretation and Definitions -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          1. Interpretation and Definitions
        </h2>

        <h3 class="text-xl font-semibold text-gray-800 mb-3">Interpretation</h3>
        <p class="text-gray-700 leading-relaxed mb-6">
          Words with capitalized initial letters have meanings defined under the following
          conditions. These definitions apply equally whether they appear in singular or
          plural form.
        </p>

        <h3 class="text-xl font-semibold text-gray-800 mb-3">Definitions</h3>
        <p class="text-gray-700 mb-4">
          For the purposes of these Terms and Conditions:
        </p>

        <ul class="space-y-4">
          <li
            v-for="definition in definitions"
            :key="definition.term"
            class="bg-white rounded-lg p-4 shadow-sm border border-gray-100"
          >
            <span class="font-semibold text-gray-900">{{ definition.term }}: </span>
            <span class="text-gray-700">{{ definition.description }}</span>
          </li>
        </ul>
      </section>

      <!-- Section 2: Acknowledgment -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          2. Acknowledgment
        </h2>
        <div class="space-y-4">
          <p
            v-for="(paragraph, index) in acknowledgmentParagraphs"
            :key="index"
            class="text-gray-700 leading-relaxed"
          >
            {{ paragraph }}
          </p>
        </div>
      </section>

      <!-- Section 3: Subscriptions -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          3. Subscriptions
        </h2>

        <div class="space-y-6">
          <div
            v-for="sub in subscriptionSections"
            :key="sub.title"
            class="bg-white rounded-lg p-5 border border-gray-100 shadow-sm"
          >
            <h3 class="text-lg font-semibold text-gray-900 mb-3">
              {{ sub.title }}
            </h3>
            <div class="space-y-3">
              <p
                v-for="(paragraph, index) in sub.paragraphs"
                :key="index"
                class="text-gray-700 leading-relaxed text-sm"
              >
                {{ paragraph }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 4: Free Trial -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          4. Free Trial
        </h2>
        <div class="bg-blue-50 border border-blue-100 rounded-lg p-5 space-y-3">
          <p
            v-for="(paragraph, index) in freeTrialParagraphs"
            :key="index"
            class="text-gray-700 leading-relaxed text-sm"
          >
            {{ paragraph }}
          </p>
        </div>
      </section>

      <!-- Section 5: Promotions -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          5. Promotions
        </h2>
        <div class="space-y-3">
          <p
            v-for="(paragraph, index) in promotionsParagraphs"
            :key="index"
            class="text-gray-700 leading-relaxed"
          >
            {{ paragraph }}
          </p>
        </div>
      </section>

      <!-- Section 6: User Accounts -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          6. User Accounts
        </h2>
        <ul class="space-y-4">
          <li
            v-for="rule in userAccountRules"
            :key="rule.title"
            class="flex gap-3 bg-white p-4 rounded-lg border border-gray-100 shadow-sm"
          >
            <span class="text-blue-500 mt-0.5 flex-shrink-0">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1
                  0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414
                  1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>
            <div>
              <span class="font-semibold text-gray-900">{{ rule.title }}: </span>
              <span class="text-gray-700 text-sm">{{ rule.description }}</span>
            </div>
          </li>
        </ul>
      </section>

      <!-- Section 7: Intellectual Property -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          7. Intellectual Property
        </h2>
        <div class="space-y-4">
          <p
            v-for="(paragraph, index) in intellectualPropertyParagraphs"
            :key="index"
            class="text-gray-700 leading-relaxed"
          >
            {{ paragraph }}
          </p>
        </div>
      </section>

      <!-- Section 8: Feedback -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          8. Your Feedback to Us
        </h2>
        <div class="bg-white rounded-lg p-5 border border-gray-100 shadow-sm">
          <p class="text-gray-700 leading-relaxed">
            {{ feedbackText }}
          </p>
        </div>
      </section>

      <!-- Section 9: Links to Other Websites -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          9. Links to Other Websites
        </h2>
        <div class="space-y-4">
          <p
            v-for="(paragraph, index) in externalLinksParagraphs"
            :key="index"
            class="text-gray-700 leading-relaxed"
          >
            {{ paragraph }}
          </p>
        </div>
      </section>

      <!-- Section 10: Termination -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          10. Termination
        </h2>
        <div class="space-y-4">
          <p
            v-for="(paragraph, index) in terminationParagraphs"
            :key="index"
            class="text-gray-700 leading-relaxed"
          >
            {{ paragraph }}
          </p>
        </div>
      </section>

      <!-- Section 11: Limitation of Liability -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          11. Limitation of Liability
        </h2>
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-5 space-y-4">
          <p
            v-for="(paragraph, index) in liabilityParagraphs"
            :key="index"
            class="text-gray-700 leading-relaxed text-sm"
          >
            {{ paragraph }}
          </p>
        </div>
      </section>

      <!-- Section 12: AS IS Disclaimer -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          12. "AS IS" and "AS AVAILABLE" Disclaimer
        </h2>
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-5 space-y-4">
          <p
            v-for="(paragraph, index) in disclaimerParagraphs"
            :key="index"
            class="text-gray-700 leading-relaxed text-sm"
          >
            {{ paragraph }}
          </p>
        </div>
      </section>

      <!-- Section 13: Governing Law -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          13. Governing Law
        </h2>
        <div class="bg-white rounded-lg p-5 border border-gray-100 shadow-sm">
          <p class="text-gray-700 leading-relaxed">
            {{ governingLawText }}
          </p>
        </div>
      </section>

      <!-- Section 14: Disputes Resolution -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          14. Disputes Resolution
        </h2>
        <div class="space-y-4">
          <div
            v-for="dispute in disputeSections"
            :key="dispute.title"
            class="bg-white rounded-lg p-5 border border-gray-100 shadow-sm"
          >
            <h3 class="text-lg font-semibold text-gray-900 mb-2">
              {{ dispute.title }}
            </h3>
            <p class="text-gray-700 leading-relaxed text-sm">
              {{ dispute.description }}
            </p>
          </div>
        </div>
      </section>

      <!-- Section 15: Severability and Waiver -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          15. Severability and Waiver
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="item in severabilityItems"
            :key="item.title"
            class="bg-white rounded-lg p-5 border border-gray-100 shadow-sm"
          >
            <h3 class="text-lg font-semibold text-gray-900 mb-2">
              {{ item.title }}
            </h3>
            <p class="text-gray-700 leading-relaxed text-sm">
              {{ item.description }}
            </p>
          </div>
        </div>
      </section>

      <!-- Section 16: Translation -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          16. Translation Interpretation
        </h2>
        <div class="bg-blue-50 border border-blue-100 rounded-lg p-5">
          <p class="text-gray-700 leading-relaxed">
            {{ translationText }}
          </p>
        </div>
      </section>

      <!-- Section 17: Changes to Terms -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          17. Changes to These Terms and Conditions
        </h2>
        <div class="space-y-4">
          <p
            v-for="(paragraph, index) in changesToTermsParagraphs"
            :key="index"
            class="text-gray-700 leading-relaxed"
          >
            {{ paragraph }}
          </p>
        </div>
      </section>

      <!-- Section 18: Contact Us -->
      <section class="mb-10">
        <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          18. Contact Us
        </h2>
        <p class="text-gray-700 mb-5">
          If you have any questions about these Terms and Conditions, you can reach us
          through any of the following channels:
        </p>

        <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <ul class="divide-y divide-gray-100">
            <li
              v-for="contact in contactDetails"
              :key="contact.label"
              class="flex items-start gap-4 p-4"
            >
              <span class="text-blue-500 mt-0.5 flex-shrink-0">
                <!-- Email Icon -->
                <svg
                  v-if="contact.icon === 'email'"
                  class="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2
                    0 00-1.997 1.884z"
                  />
                  <path
                    d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"
                  />
                </svg>
                <!-- Phone Icon -->
                <svg
                  v-else-if="contact.icon === 'phone'"
                  class="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1
                    1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1
                    1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82
                    18 2 12.18 2 5V3z"
                  />
                </svg>
                <!-- Globe Icon -->
                <svg
                  v-else-if="contact.icon === 'web'"
                  class="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012
                    6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0
                    019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0
                    0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973
                    5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0
                    00-1.668-1.973z"
                    clip-rule="evenodd"
                  />
                </svg>
                <!-- Location Icon -->
                <svg
                  v-else-if="contact.icon === 'location'"
                  class="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0
                    010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>

              <div>
                <span class="block text-sm font-semibold text-gray-500 mb-0.5">
                  {{ contact.label }}
                </span>
                <a
                  v-if="contact.href"
                  :href="contact.href"
                  class="text-blue-600 hover:underline text-sm"
                >
                  {{ contact.value }}
                </a>
                <span v-else class="text-gray-700 text-sm">
                  {{ contact.value }}
                </span>
              </div>
            </li>
          </ul>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
export default {
  name: "TermsPage",

  data() {
    return {

      definitions: [
        {
          term: "Account",
          description:
            "A unique account created for you to access our Service or parts of our Service.",
        },
        {
          term: "Application",
          description:
            "The software program provided by the Company downloaded by you on any electronic device, named Agora Deng.",
        },
        {
          term: "Application Store",
          description:
            "The digital distribution service operated by Apple Inc. (Apple App Store) or Google Inc. (Google Play Store) from which the Application has been downloaded.",
        },
        {
          term: "Affiliate",
          description:
            'An entity that controls, is controlled by, or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest, or other voting securities.',
        },
        {
          term: "Country",
          description: "Iraq.",
        },
        {
          term: "Company",
          description:
            'Referred to as "We," "Us," or "Our" — Agora Vision Company for Digital Broadcasting and Publishing Ltd., Heeran Building, next to Azadi Mall, Offices No. 5 & 6, Sulaimani, Iraq.',
        },
        {
          term: "Device",
          description:
            "Any device that can access the Service, such as a computer, cellphone, or digital tablet.",
        },
        {
          term: "Feedback",
          description:
            "Feedback, innovations, or suggestions sent by you regarding the attributes, performance, or features of our Service.",
        },
        {
          term: "Free Trial",
          description:
            "A limited period of time that may be offered free of charge when purchasing a Subscription.",
        },
        {
          term: "Promotions",
          description:
            "Contests, sweepstakes, or other promotional activities offered through the Service.",
        },
        {
          term: "Service",
          description: "Refers to the Application, the Website, or both.",
        },
        {
          term: "Subscriptions",
          description:
            "Services or access to the Service offered on a subscription basis by the Company to you.",
        },
        {
          term: "Terms and Conditions",
          description:
            'Also referred to as "Terms" — the entire agreement between you and the Company regarding the use of the Service.',
        },
        {
          term: "Third-Party Social Media Service",
          description:
            "Any services or content provided by a third party that may be displayed, included, or made available through the Service.",
        },
        {
          term: "Website",
          description:
            "Agora Deng, accessible from https://agoradeng.site/",
        },
        {
          term: "You",
          description:
            "The individual accessing or using the Service, or the company or legal entity on behalf of which such individual is acting.",
        },
      ],

      acknowledgmentParagraphs: [
        "These Terms and Conditions govern the use of this Service and constitute the entire agreement between you and the Company. They set out the rights and obligations of all users regarding the use of the Service.",
        "Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms and Conditions. These Terms apply to all visitors, users, and anyone else who accesses or uses the Service.",
        "By accessing or using the Service, you agree to be bound by these Terms and Conditions. If you disagree with any part of these Terms, you may not access the Service.",
        "You represent that you are over the age of 18. The Company does not permit those under 18 to use the Service.",
        "Your access to and use of the Service is also conditioned on your acceptance of and compliance with our Privacy Policy. Our Privacy Policy describes how we collect, use, and disclose your personal information when you use the Application or Website. Please read our Privacy Policy carefully before using our Service.",
      ],

      subscriptionSections: [
        {
          title: "Subscription Period",
          paragraphs: [
            "The Service or parts of the Service are available only with a paid Subscription. You will be billed in advance on a recurring and periodic basis (daily, weekly, monthly, or annually), depending on the Subscription plan you select.",
            "At the end of each billing period, your Subscription will automatically renew under the same conditions unless you cancel it or the Company cancels it.",
          ],
        },
        {
          title: "Subscription Cancellations",
          paragraphs: [
            "You may cancel your Subscription renewal through your Account settings page or by contacting the Company. You will not receive a refund for fees already paid for your current Subscription period, but you will retain access to the Service until the end of that period.",
          ],
        },
        {
          title: "Billing",
          paragraphs: [
            "You must provide accurate and complete billing information, including your full name, address, state, ZIP code, telephone number, and a valid payment method.",
            "If automatic billing fails for any reason, the Company will issue an electronic invoice requiring manual payment in full within a specified deadline, as indicated on the invoice.",
          ],
        },
        {
          title: "Fee Changes",
          paragraphs: [
            "The Company may, at its sole discretion and at any time, modify Subscription fees. Any fee change will take effect at the end of your current Subscription period.",
            "The Company will provide reasonable prior notice of any fee change so you have the opportunity to cancel your Subscription before the change becomes effective.",
            "Your continued use of the Service after a fee change takes effect constitutes your agreement to pay the updated Subscription fee.",
          ],
        },
        {
          title: "Refunds",
          paragraphs: [
            "Except where required by law, paid Subscription fees are non-refundable.",
            "Certain refund requests may be considered by the Company on a case-by-case basis and granted at the sole discretion of the Company.",
          ],
        },
      ],

      freeTrialParagraphs: [
        "The Company may, at its sole discretion, offer a Subscription with a Free Trial for a limited period of time.",
        "You may be required to enter your billing information to sign up for the Free Trial.",
        "If you enter billing information during sign-up, you will not be charged until the Free Trial expires. On the last day of the Free Trial period, unless you have cancelled your Subscription, you will be automatically charged the applicable Subscription fee for your selected plan.",
        "At any time and without notice, the Company reserves the right to modify the terms of the Free Trial offer or cancel it entirely.",
      ],

      promotionsParagraphs: [
        "Any Promotions made available through the Service may be governed by rules that are separate from these Terms.",
        "If you participate in any Promotions, please review the applicable rules as well as our Privacy Policy. If the rules for a Promotion conflict with these Terms, the Promotion rules will apply.",
      ],

      userAccountRules: [
        {
          title: "Accurate Information",
          description:
            "When creating an account, you must provide accurate, complete, and up-to-date information. Failure to do so constitutes a breach of these Terms and may result in immediate account termination.",
        },
        {
          title: "Password Responsibility",
          description:
            "You are responsible for safeguarding your password and for all activities or actions taken under your account, whether on our Service or a Third-Party Social Media Service.",
        },
        {
          title: "Confidentiality",
          description:
            "You agree not to disclose your password to any third party. You must notify us immediately if you become aware of any security breach or unauthorized use of your account.",
        },
        {
          title: "Username Policy",
          description:
            "You may not use a username that belongs to another person or entity, is subject to trademark rights without authorization, or is otherwise offensive, vulgar, or obscene.",
        },
      ],

      intellectualPropertyParagraphs: [
        "The Service and its original content (excluding content provided by you or other users), features, and functionality are and will remain the exclusive property of the Company and its licensors.",
        "The Service is protected by copyright, trademark, and other applicable laws of both Iraq and foreign countries.",
        "Our trademarks and trade dress may not be used in connection with any product or service without prior written consent from the Company.",
      ],

      feedbackText:
        "You assign all rights, title, and interest in any Feedback you provide to the Company. If for any reason such assignment is ineffective, you agree to grant the Company a non-exclusive, perpetual, irrevocable, royalty-free, worldwide license to use, reproduce, disclose, sublicense, distribute, modify, and exploit such Feedback without restriction.",

      externalLinksParagraphs: [
        "Our Service may contain links to third-party websites or services that are not owned or controlled by the Company.",
        "The Company has no control over and assumes no responsibility for the content, privacy policies, or practices of any third-party websites or services. You acknowledge and agree that the Company shall not be responsible or liable, directly or indirectly, for any damage or loss caused by or in connection with your use of or reliance on any such content, goods, or services.",
        "We strongly advise you to read the Terms and Conditions and Privacy Policies of any third-party websites or services you visit.",
      ],

      terminationParagraphs: [
        "We may terminate or suspend your Account immediately, without prior notice or liability, for any reason, including if you breach these Terms and Conditions.",
        "Upon termination, your right to use the Service will cease immediately. If you wish to terminate your Account, you may simply discontinue using the Service.",
      ],

      liabilityParagraphs: [
        "Notwithstanding any damages you might incur, the entire liability of the Company and its suppliers under any provision of these Terms, and your exclusive remedy, shall be limited to the amount actually paid by you through the Service, or 100 USD if you have not made any purchases through the Service.",
        "To the maximum extent permitted by applicable law, the Company and its suppliers shall not be liable for any special, incidental, indirect, or consequential damages whatsoever — including damages for loss of profits, loss of data, business interruption, personal injury, or loss of privacy — arising out of or in any way related to the use of or inability to use the Service, even if the Company has been advised of the possibility of such damages.",
        "Some jurisdictions do not allow the exclusion of implied warranties or limitation of liability for incidental or consequential damages, so some of the above limitations may not apply to you. In such cases, liability will be limited to the greatest extent permitted by law.",
      ],

      disclaimerParagraphs: [
        'The Service is provided to you "AS IS" and "AS AVAILABLE," with all faults and defects and without warranty of any kind. To the maximum extent permitted by applicable law, the Company expressly disclaims all warranties — whether express, implied, statutory, or otherwise — including implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement.',
        "The Company makes no warranty that the Service will meet your requirements, achieve any intended results, be compatible with other software or systems, operate without interruption, meet any performance or reliability standards, or be error-free.",
        "Neither the Company nor any of its providers makes any representation or warranty: (i) as to the operation or availability of the Service; (ii) that the Service will be uninterrupted or error-free; (iii) as to the accuracy or reliability of any content provided through the Service; or (iv) that the Service, its servers, or related emails are free of viruses, malware, or other harmful components.",
        "Some jurisdictions do not allow certain warranty exclusions, so some of the above may not apply to you. In such cases, exclusions will be applied to the greatest extent enforceable under applicable law.",
      ],

      governingLawText:
        "The laws of Iraq, excluding its conflict of law rules, shall govern these Terms and your use of the Service. Your use of the Application may also be subject to other local, national, or international laws.",

      disputeSections: [
        {
          title: "General Disputes",
          description:
            "If you have any concern or dispute about the Service, you agree to first attempt to resolve it informally by contacting the Company directly.",
        },
        {
          title: "European Union (EU) Users",
          description:
            "If you are a European Union consumer, you will benefit from any mandatory provisions of the law of the country in which you reside.",
        },
        {
          title: "United States Federal Government End Use",
          description:
            'If you are a U.S. federal government end user, our Service is a "Commercial Item" as defined at 48 C.F.R. §2.101.',
        },
        {
          title: "United States Legal Compliance",
          description:
            'You represent and warrant that (i) you are not located in a country subject to a U.S. government embargo or designated as a "terrorist supporting" country, and (ii) you are not listed on any U.S. government list of prohibited or restricted parties.',
        },
      ],

      severabilityItems: [
        {
          title: "Severability",
          description:
            "If any provision of these Terms is found to be unenforceable or invalid, it will be modified and interpreted to accomplish its objectives to the greatest extent possible under applicable law. All remaining provisions will continue in full force and effect.",
        },
        {
          title: "Waiver",
          description:
            "Except as provided herein, the failure to exercise a right or require performance of an obligation under these Terms shall not affect a party's ability to exercise such right or require such performance at any later time. A waiver of a breach does not constitute a waiver of any subsequent breach.",
        },
      ],

      translationText:
        "These Terms and Conditions may have been translated into other languages and made available on our Service. In the event of a dispute, the original English text shall prevail.",

      changesToTermsParagraphs: [
        "We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will make reasonable efforts to provide at least 30 days' notice before the new terms take effect. What constitutes a material change will be determined at our sole discretion.",
        "By continuing to access or use our Service after revisions become effective, you agree to be bound by the updated terms. If you do not agree to the new terms, in whole or in part, please stop using the Service.",
      ],

      contactDetails: [
        {
          label: "Email",
          value: "slaw@agoravision.com",
          href: "mailto:slaw@agoravision.com",
          icon: "email",
        },
        {
          label: "Website",
          value: "https://www.agoravision.com/",
          href: "https://www.agoravision.com/",
          icon: "web",
        },
        {
          label: "Phone",
          value: "+964 770 2000 545 | +964 750 2000 545",
          href: null,
          icon: "phone",
        },
        {
          label: "Mailing Address",
          value:
            "Heeran Building, next to Azadi Mall, Offices No. 5 & 6, Sulaimani, Iraq",
          href: null,
          icon: "location",
        },
      ],
    };
  },
};
</script>